import React from "react";
import { Link } from "react-router-dom";
import { useFormik } from "formik";
import moment from "moment";

import { ReportOperatorSalary } from "../../../models/ReportOperatorSalary";
import axiosClient from "../../../config/axios";

import NewOperatorSalaryFormGruas from "../../../forms/NewOperatorSalaryFormGruas";
import ReportOperatorSalarySchema from "../../../validations/ReportOperatorSalarySchema";
import TableOperatorCommissionBySales from "../../../components/reports/TableOperatorCommissionBySales";
import CommissionPriceRanges from "../../../components/reports/CommissionPriceRanges";
import Loader from "../../../components/loader";
import { toCamelCase } from "../../../utils";

const NewOperatorSalaryGruas = () => {
  const [loading, setLoading] = React.useState(true);
  const [records, setRecords] = React.useState([]);
  const [orders, setOrders] = React.useState([]);
  const [operators, setOperators] = React.useState([]);
  const [status, setStatus] = React.useState([]);
  const [serviceTypes, setServiceTypes] = React.useState([]);
  const [priceRanges, setPriceRanges] = React.useState([
    {
      minimo: 1,
      maximo: 3000,
      porcentaje: 3,
    },
    {
      minimo: 3001,
      maximo: 5000,
      porcentaje: 2,
    },
    {
      minimo: 5001,
      maximo: null, // Representa "en adelante" o sin límite
      porcentaje: 1,
    },
  ]);

  // Ref para rastrear si ya se hizo la primera carga
  const isInitialMount = React.useRef(true);
  // Ref para rastrear si los datos ya se cargaron
  const hasDataLoaded = React.useRef(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      ...ReportOperatorSalary(),
      orderType: "Normal",
    },
    validationSchema: ReportOperatorSalarySchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const adjustedStart = moment(values.start).format("YYYY-MM-DD");
        const adjustedEnd = moment(values.end).format("YYYY-MM-DD");
        const dataTable = [];

        // === Ejecutar solo si orderType incluye "Normal" ===
        if (values.orderType === "Normal" || values.orderType === "all") {
          const endpointNormal = `/reports/operator-salary?start=${adjustedStart}&end=${adjustedEnd}&status=${values.status}&id_operador=${values.operator}`;
          const { data: normalData } = await axiosClient.get(endpointNormal);

          normalData?.forEach((record) => {
            const { operador } = record;
            record.orders.forEach((order) => {
              const { Order } = order;
              dataTable.push({
                ...Order,
                subtotal: Order.orden_en_cero ? 0 : Order.subtotal,
                orden_en_cero: Order.orden_en_cero || false,
                operador,
                totalDistance: order.totalDistance,
                tipo: "Normal",
              });
            });
          });
        }

        // === Ejecutar solo si orderType incluye "Varo Express" ===
        if (values.orderType === "Varo Express" || values.orderType === "all") {
          const statusParam =
            values.status && values.status !== "all"
              ? `&status=${values.status}`
              : "";
          const endpointVial = `/order-vial/datatable?draw=1&start=0&length=10000&dateFrom=${adjustedStart}&dateTo=${adjustedEnd}${statusParam}`;
          const { data: vialData } = await axiosClient.get(endpointVial);

          vialData.data.forEach((vialOrder) => {
            const operador = vialOrder.operator || vialOrder.chofer_auxiliar;

            dataTable.push({
              id: vialOrder.id,
              folio: vialOrder.folio,
              subtotal: parseFloat((vialOrder.total / 1.16).toFixed(2)),
              orden_en_cero: false,
              fecha_hora_reporte: vialOrder.fecha_hora_orden,
              grua: vialOrder.grua,
              operador,
              Crane: vialOrder.Crane,
              tipoServicio: vialOrder?.typeService?.type_service_name,
              tipo: "Varo Express",
              razon: `Varo Express: ${
                vialOrder?.typeService?.type_service_name || ""
              }`,
              comisionPorGrua: 10,
              totalDistance: 0,
            });
          });
        }

        // === Ordenar por fecha (más antigua → más reciente) ===
        dataTable.sort(
          (a, b) =>
            new Date(a.fecha_hora_reporte) - new Date(b.fecha_hora_reporte)
        );

        // === Filtrar las órdenes según el tipo seleccionado ===
        const filteredOrders =
          values.orderType && values.orderType !== "all"
            ? dataTable.filter((order) => order.tipo === values.orderType)
            : dataTable;

        setOrders(filteredOrders);

        // === Agrupar por operador (para exportar a Excel) ===
        const groupedRecords = {};

        filteredOrders.forEach((order) => {
          const operador = order.operador;
          const operadorId = operador?.id;
          if (!operadorId) return;

          if (!groupedRecords[operadorId]) {
            groupedRecords[operadorId] = {
              operador,
              orders: [],
            };
          }

          groupedRecords[operadorId].orders.push({
            Order: {
              id: order.id,
              folio: order.folio,
              subtotal: order.subtotal,
              orden_en_cero: order.orden_en_cero || false,
              fecha_hora_reporte: order.fecha_hora_reporte,
              grua: order.grua,
              auxilio_vial: order.auxilio_vial,
              montacarga: order.montacarga,
              corresponsalia: order.corresponsalia,
              taxi: order.taxi,
              Crane: order.Crane,
              tipoServicio: order.tipoServicio,
              tipo: order.tipo,
              razon: order.razon,
              comisionPorGrua: order.comisionPorGrua,
            },
            totalDistance: order.totalDistance || 0,
          });
        });

        // === Convertir a arreglo y ordenar las órdenes dentro de cada operador ===
        const combinedRecords = Object.values(groupedRecords).map((record) => {
          record.orders.sort(
            (a, b) =>
              new Date(a.Order?.fecha_hora_reporte) -
              new Date(b.Order?.fecha_hora_reporte)
          );
          return record;
        });

        setRecords(combinedRecords);
        // Marcar que los datos se han cargado
        hasDataLoaded.current = true;
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    },
  });

  const fetchData = async () => {
    try {
      const response = await axiosClient.get("/users/allOperators-active");

      const formattedOptions = [
        { value: "all", label: "Todas las opciones" },
        ...response.data.data.map((operator) => ({
          value: operator.id,
          label: toCamelCase(operator.name + " " + operator.first_surname),
        })),
      ];

      setOperators(formattedOptions);

      const responseStatus = await axiosClient.get(
        "/orderStatuses/allWithOrderCount"
      );

      const formattedOptionsStatus = [
        { value: "all", label: "Todas las opciones" },
        ...responseStatus.data.data.map((operator) => ({
          value: operator.id,
          label: operator.order_status,
        })),
      ];

      setStatus(formattedOptionsStatus);

      // Obtener tipos de servicio desde el endpoint
      const { data: typeServicesResponse } = await axiosClient.get(
        "/typeServices/datatable"
      );
      setServiceTypes(
        typeServicesResponse.data.map((type) => ({
          value: type.id,
          label: type.type_service_name,
        }))
      );
    } catch (error) {
      console.error(error);
    }
  };

  // Función para manejar cambios en los rangos de precios
  const handlePriceRangeChange = React.useCallback((newPriceRanges) => {
    setPriceRanges(newPriceRanges);
  }, []);

  React.useEffect(() => {
    const loadAndSubmit = async () => {
      await fetchData();
      formik.handleSubmit();
    };
    loadAndSubmit();
  }, []);

  return (
    <React.Fragment>
      <Loader isLoading={loading} />
      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Inicio</Link>
            </li>
            <li className="breadcrumb-item active">
              Reporte de comisiones a operadores
            </li>
          </ol>
          <h1 className="page-header mb-0">Reporte de comisiones operadores</h1>
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-xl-12 card border-0 p-3">
          <NewOperatorSalaryFormGruas
            formik={formik}
            records={records}
            status={status}
            operators={operators}
            priceRanges={priceRanges}
          />
        </div>
      </div>
      <div className="row mb-3">
        <div className="col-xl-12 card border-0 p-3">
          <h5 className="mb-3">Rangos de precios de comisiones</h5>
          <CommissionPriceRanges
            priceRanges={priceRanges}
            onPriceRangeChange={handlePriceRangeChange}
          />
        </div>
      </div>
      <div className="row">
        <div className="col-xl-12 card border-0 p-3">
          <TableOperatorCommissionBySales
            orders={orders}
            start={formik.values.start}
            end={formik.values.end}
            priceRanges={priceRanges}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default NewOperatorSalaryGruas;
